import { useState } from 'react';
import { esopApi } from '../services/api';
import '../styles/Modal.css';

function GenerateESOPModal({ entity, onClose, onSuccess }) {
  const currentYear = new Date().getFullYear();
  const [periods, setPeriods] = useState([
    {
      start_date: `${currentYear}-04-01`,
      end_date: `${currentYear + 1}-03-31`
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleAddPeriod = () => {
    const lastPeriod = periods[periods.length - 1];
    const lastEndDate = new Date(lastPeriod.end_date);

    // Next period starts the day after the last one ends
    const nextStartDate = new Date(lastEndDate);
    nextStartDate.setDate(nextStartDate.getDate() + 1);

    // Default to 1 year period
    const nextEndDate = new Date(nextStartDate);
    nextEndDate.setFullYear(nextEndDate.getFullYear() + 1);
    nextEndDate.setDate(nextEndDate.getDate() - 1);

    setPeriods([
      ...periods,
      {
        start_date: nextStartDate.toISOString().split('T')[0],
        end_date: nextEndDate.toISOString().split('T')[0]
      }
    ]);
  };

  const handleRemovePeriod = (index) => {
    if (periods.length > 1) {
      setPeriods(periods.filter((_, i) => i !== index));
    }
  };

  const handlePeriodChange = (index, field, value) => {
    const updated = [...periods];
    updated[index][field] = value;
    setPeriods(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all periods
    for (let i = 0; i < periods.length; i++) {
      const period = periods[i];

      if (!period.start_date || !period.end_date) {
        setError(`Please fill in all dates for period ${i + 1}`);
        return;
      }

      const start = new Date(period.start_date);
      const end = new Date(period.end_date);

      if (start >= end) {
        setError(`Invalid date range for period ${i + 1}: Start date must be before end date`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await esopApi.generate(entity.name, periods);

      setSuccess(
        `ESOP workings generated successfully!\n\n` +
        `Grants Processed: ${result.summary?.grants_processed || 0}\n` +
        `Periods: ${result.summary?.fiscal_years || periods.length}\n` +
        `Output: ${result.period}`
      );

      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to generate ESOP workings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Generate ESOP Workings</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Entity:</label>
              <input
                type="text"
                value={entity.name}
                disabled
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Reporting Periods:</label>
              <small className="form-help">
                Define the periods for which ESOP expense should be calculated.
                You can add multiple periods (fiscal years, quarters, or custom date ranges).
              </small>

              {periods.map((period, index) => (
                <div key={index} className="fiscal-year-row">
                  <div className="fiscal-year-inputs">
                    <div className="input-group">
                      <label>Period {index + 1} - Start Date:</label>
                      <input
                        type="date"
                        value={period.start_date}
                        onChange={(e) => handlePeriodChange(index, 'start_date', e.target.value)}
                        className="form-control"
                        disabled={loading}
                      />
                    </div>

                    <div className="input-group">
                      <label>End Date:</label>
                      <input
                        type="date"
                        value={period.end_date}
                        onChange={(e) => handlePeriodChange(index, 'end_date', e.target.value)}
                        className="form-control"
                        disabled={loading}
                      />
                    </div>

                    {periods.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleRemovePeriod(index)}
                        disabled={loading}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleAddPeriod}
                disabled={loading}
                style={{ marginTop: '10px' }}
              >
                + Add Another Period
              </button>
            </div>

            <div className="info-box">
              <strong>Output will include:</strong>
              <ul>
                <li>Grant Chart with vesting matrix for all employees</li>
                <li>ESOP Expense Summary by Grant Date</li>
                <li>Period-wise ESOP Expense allocation across fiscal years</li>
                <li>Journal Entry Summary for each period</li>
              </ul>
            </div>

            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}

            {success && (
              <div className="alert alert-success" style={{ whiteSpace: 'pre-line' }}>
                {success}
              </div>
            )}

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Generating...' : 'Generate Workings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default GenerateESOPModal;
